/**
 * Hook useAnalytics
 * Tracking automatique des page views (RGPD compliant)
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { logVisit } from '@e-store/shared';

/**
 * Générer ou récupérer le sessionId (anonyme)
 */
function getSessionId(): string {
  const key = 'analytics_session';
  let sessionId = sessionStorage.getItem(key);

  if (!sessionId) {
    sessionId = uuidv4();
    sessionStorage.setItem(key, sessionId);
  }

  return sessionId;
}

/**
 * Détecter le type d'appareil
 */
function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const width = window.innerWidth;

  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * Hook pour tracker les page views automatiquement
 */
export function useAnalytics() {
  const location = useLocation();
  const entryTimeRef = useRef<number>(Date.now());
  const lastPathRef = useRef<string>('');

  useEffect(() => {
    const sessionId = getSessionId();
    const deviceType = getDeviceType();
    const currentPath = location.pathname + location.search;

    // Logger la page précédente avec durée
    if (lastPathRef.current && lastPathRef.current !== currentPath) {
      const duration = Date.now() - entryTimeRef.current;

      // Batch les logs (ne pas bloquer la navigation)
      setTimeout(() => {
        logVisit({
          sessionId,
          path: lastPathRef.current,
          referrer: document.referrer || undefined,
          userAgent: navigator.userAgent,
          deviceType,
          duration,
        }).catch((err) => {
          // Silent fail
          console.debug('Analytics error:', err);
        });
      }, 100);
    }

    // Préparer pour la prochaine page
    lastPathRef.current = currentPath;
    entryTimeRef.current = Date.now();

    // Cleanup: logger la dernière page au unmount
    return () => {
      if (lastPathRef.current) {
        const duration = Date.now() - entryTimeRef.current;

        // Utiliser sendBeacon pour l'envoi asynchrone (ne bloque pas la fermeture)
        const payload = {
          sessionId,
          path: lastPathRef.current,
          referrer: document.referrer || undefined,
          userAgent: navigator.userAgent,
          deviceType,
          duration,
        };

        // Fallback sur fetch si sendBeacon n'est pas disponible
        if (navigator.sendBeacon) {
          const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
          navigator.sendBeacon(`${import.meta.env.VITE_API_URL}/api/analytics/visit`, blob);
        } else {
          logVisit(payload).catch(() => {});
        }
      }
    };
  }, [location]);
}
